import {
    useCentralErrorSetter,
    useGetErrorInfo,
} from '@experiences/error';
import type {
    IGridResource,
    IResource,
} from '@experiences/interfaces';
import {
    UiCopyButton,
    UiDataContextProvider,
    UiStack,
    UiSuspensefulOutlet,
    UiText,
    useUiDataContext,
} from '@experiences/ui-common';
import {
    getDateFromNow,
    getDisplayName,
    secondsToDays,
    timestampToDate,
    useNavigateWithParams,
    useRouteResolver,
    useShowDialog,
} from '@experiences/util';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import Tokens, { FontVariantToken } from '@uipath/apollo-core';
import {
    ApButton,
    ApDataGridColumn,
    ApDataGridFooter,
    ApDataGridWrapper,
    ApProgressSpinner,
    ApTextField,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    FormProvider,
    useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import useSWR from 'swr';

import * as RouteNames from '../../../common/constants/RouteNames';
import { useGetSetting } from '../../../common/hooks/useGetSetting';
import type { IReferenceToken } from '../../../common/interfaces/cis/profile';
import {
    externalApiResourceUrl,
    getExternalApiResources,
} from '../../../services/identity/ExternalApiResourceService';
import {
    deleteReferenceToken,
    generateReferenceToken,
    getReferenceToken,
    referenceTokenUrl,
} from '../../../services/identity/ReferenceTokenService';
import { useTelemetryHelper } from '../../../telemetry/TelemetryHelper';
import { mapTokenScopesToResources } from '../../../util/TokenUtil';
import UiForm from '../../common/UiForm';
import UiPageContainer from '../../common/UiPageContainer/UiPageContainer';
import type { IResourceForm } from '../../externalApps/subcomponents/ScopesController';
import DisplayAccessToken from './DisplayAccessToken';

interface IGridResourceWithId extends IGridResource {
    id: string;
}

interface IPATDataContext {
    selectedScopes: Array<{
        resource: string;
        scopes: string[];
    }>;
}

interface IReferenceTokenForm extends IResourceForm {
    tokenName: string;
    expirationDate: number;
}

const LIFETIME_DEFAULT = 360;

const useStyles = makeStyles(() =>
    createStyles({
        actionsContainer: {
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: Tokens.Spacing.SpacingS,
            paddingRight: Tokens.Spacing.SpacingL,
        },
        descriptionText: {
            paddingTop: Tokens.Spacing.SpacingS,
            paddingBottom: Tokens.Spacing.SpacingXl,
        },
        formFieldsContainer: { gap: Tokens.Spacing.SpacingL },
        formField: { flex: 1 },
        ownerFieldContainer: {
            gap: Tokens.Spacing.SpacingL,
            marginBottom: Tokens.Spacing.SpacingL,
        },
        ownerField: { flex: 1 },
        resourcesHeader: {
            paddingTop: Tokens.Spacing.SpacingXxl,
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        resourcesDescription: { marginBottom: Tokens.Spacing.SpacingS },
        scopeCell: {
            width: '100%',
            gap: Tokens.Spacing.SpacingXs,
        },
        scopeText: {
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
    }),
);

const CreatePersonalAccessTokenForm: React.FC = () => {
    const classes = useStyles();
    const location = useLocation();
    const { formatMessage: translate } = useIntl();
    const getRoute = useRouteResolver();
    const navigate = useNavigateWithParams();
    const createDialog = useShowDialog();
    const { getErrorMessage } = useGetErrorInfo();
    const setErrorMessage = useCentralErrorSetter();
    const [ lifetimeMax, setLifetimeMax ] = useState(LIFETIME_DEFAULT);
    const [ loading, setLoading ] = useState(false);

    const { logEvent } = useTelemetryHelper();

    const {
        data: patData, setData: setPATData,
    } = useUiDataContext<IPATDataContext>();

    const { mutate } = useSWR<IReferenceToken[], Error>(referenceTokenUrl, getReferenceToken);
    const { data: externalResources } = useSWR<IResource[], Error>(externalApiResourceUrl, getExternalApiResources);
    const isViewMode = location.pathname.includes('/view');
    const tokenData = location.state?.tokenData;
    const isAdminContext = location.state?.context === 'admin';
    const isDuplicateMode = location.state?.context === 'duplicate';

    const returnToPATPage = useCallback(() => {
        if (isAdminContext) {
            navigate(getRoute(RouteNames.ExternalApplicationsPAT));
        } else {
            navigate(getRoute(RouteNames.PersonalAccessToken));
        }
    }, [ getRoute, navigate, isAdminContext ]);

    const onAddScopes = useCallback(() => {
        navigate(getRoute(RouteNames.CreatePersonalAccessTokenScopes));
    }, [ getRoute, navigate ]);

    const methods = useForm<IReferenceTokenForm>({
        mode: 'onChange',
        defaultValues: {
            tokenName: (isViewMode || isDuplicateMode) ? tokenData?.description || '' : '',
            expirationDate: (isViewMode || isDuplicateMode) && tokenData ?
                Math.ceil((new Date(tokenData.expiration).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0,
            name: undefined,
            userScopes: (isViewMode || isDuplicateMode) ? tokenData?.scopes || [] : [],
            applicationScopes: [],
        },
    });

    const viewModeSelectedScopes = useMemo(() => {
        if (!tokenData?.scopes || (!isViewMode && !isDuplicateMode)) {
            return [];
        }

        return mapTokenScopesToResources(tokenData.scopes, externalResources);
    }, [ externalResources, tokenData?.scopes, isViewMode, isDuplicateMode ]);

    const {
        handleSubmit,
        reset,
        watch,
        setValue,
    } = methods;

    const expirationValue = watch('expirationDate');
    const tokenNameValue = watch('tokenName');
    const userScopesValue = watch('userScopes');

    const { data: result } = useGetSetting([ 'ReferenceTokensLifetimeMax' ]);

    const onRevoke = useCallback(async () => {
        if (!tokenData?.id) {
            return;
        }

        const proceed = await createDialog({
            title: translate({ id: 'CLIENT_DELETE_TOKEN' }),
            body: <div data-cy="revoke-token-message">
                {translate({ id: 'CLIENT_REVOKE_TOKEN_MESSAGE' })}
            </div>,
            icon: 'error',
            primaryButtonText: translate({ id: 'CLIENT_DELETE' }),
            showCancel: true,
        });

        if (proceed) {
            try {
                await deleteReferenceToken(tokenData.id);
                mutate();
                returnToPATPage();
            } catch (error) {
                const response = await getErrorMessage(error);
                setErrorMessage(response);
            }
        }

    }, [ createDialog, getErrorMessage, mutate, setErrorMessage, translate, tokenData, returnToPATPage ]);

    const onSubmit = useCallback(async (data: IReferenceTokenForm) => {
        setLoading(true);

        const days = Number(data.expirationDate);
        let latencyInSeconds = 0;
        if (days === 1) {
            latencyInSeconds = 5000;
        } else if (days === lifetimeMax) {
            latencyInSeconds = -5000;
        }

        const payload = {
            description: data.tokenName,
            expiration: new Date(getDateFromNow(days).getTime() + latencyInSeconds),
            scopes: data.userScopes,
        };

        try {
            const returnPayload = await generateReferenceToken(payload);

            const selectedScopes = patData?.selectedScopes || [];
            const resources = selectedScopes.map(scope => scope.resource);
            const scopes = selectedScopes.flatMap(scope => scope.scopes);

            const telemetryProperties = {
                Resources: resources.join(','),
                Scopes: scopes.join(','),
            };

            logEvent('PersonalAccessToken.Create', telemetryProperties);

            setLoading(false);
            returnToPATPage();

            await createDialog({
                title: translate({ id: 'CLIENT_TOKEN_CREATED' }),
                body: <DisplayAccessToken token={returnPayload} />,
                icon: 'success',
                primaryButtonText: translate({ id: 'CLIENT_CLOSE' }),
                showCancel: false,
            });

        } catch (error) {
            const response = await getErrorMessage(error);
            setErrorMessage(response);
        } finally {
            setLoading(false);
        }
    }, [
        lifetimeMax,
        createDialog,
        getErrorMessage,
        setErrorMessage,
        translate,
        returnToPATPage,
        logEvent,
        patData?.selectedScopes,
    ]);

    const onDeleteScope = useCallback(async (resourceName: string) => {

        if (isViewMode || isDuplicateMode) {
            const currentSelectedScopes = isDuplicateMode ? (patData?.selectedScopes || []) : viewModeSelectedScopes;
            const updatedSelectedScopes = currentSelectedScopes.filter(scope => scope.resource !== resourceName);
            setPATData({ selectedScopes: updatedSelectedScopes });
        } else {
            const updatedSelectedScopes = patData?.selectedScopes?.filter(scope => scope.resource !== resourceName) || [];
            setPATData({ selectedScopes: updatedSelectedScopes });
        }

        const currentUserScopes = watch('userScopes');
        const currentSelectedScopes = isDuplicateMode ? (patData?.selectedScopes || []) :
            (isViewMode ? viewModeSelectedScopes : patData?.selectedScopes || []);
        const scopesToRemove = currentSelectedScopes.find(scope => scope.resource === resourceName)?.scopes ?? [];

        const updatedUserScopes = currentUserScopes.filter(scope => !scopesToRemove.includes(scope));
        setValue('userScopes', updatedUserScopes, { shouldDirty: true });

    }, [
        isViewMode,
        isDuplicateMode,
        viewModeSelectedScopes,
        patData?.selectedScopes,
        setPATData,
        watch,
        setValue,
    ]);

    useEffect(() => {
        if (!result) {
            return;
        }
        const lifetimeMaxSetting = result.find(setting => setting.key === 'ReferenceTokensLifetimeMax');
        if (lifetimeMaxSetting?.value) {
            setLifetimeMax(parseInt(secondsToDays(parseInt(lifetimeMaxSetting.value))));
        }
    }, [ reset, result ]);

    useEffect(() => {
        if (!isViewMode && !isDuplicateMode) {
            const allUserScopes = patData?.selectedScopes?.flatMap(item => item.scopes) || [];
            setValue('userScopes', allUserScopes, { shouldDirty: true });
        }
    }, [ patData?.selectedScopes, setValue, isViewMode, isDuplicateMode ]);

    const allGridData = useMemo(() => {
        const selectedScopes = isViewMode ? viewModeSelectedScopes : patData?.selectedScopes;

        const scopesResult = selectedScopes?.map((item, index) => ({
            id: index.toString(),
            name: item.resource,
            description: item.resource,
            scopes: item.scopes.join(', '),
            userScopes: item.scopes,
            applicationScopes: [],
        })) || [];

        return scopesResult;
    }, [ patData?.selectedScopes, isViewMode, viewModeSelectedScopes ]);

    return (
        <UiPageContainer
            position={isAdminContext ? 'left' : 'center'}
            tenantShellHidden={!isAdminContext}
            maxWidth={isAdminContext ? '1200px' : undefined}>
            <FormProvider {...methods}>
                <UiForm
                    onSubmit={handleSubmit(onSubmit)}
                    actions={
                        isViewMode ? (
                            <UiStack
                                direction="row"
                                justify="between"
                                align="center"
                                w="100%"
                                px={Tokens.Spacing.SpacingL}>
                                <ApButton
                                    label={translate({ id: 'CLIENT_DELETE' })}
                                    variant="destructive"
                                    loading={loading}
                                    onClick={onRevoke}
                                    data-cy="revoke-token-button"
                                    disabled={!tokenData?.id}
                                />
                                <ApButton
                                    label={translate({ id: 'CLIENT_BACK' })}
                                    onClick={returnToPATPage}
                                    variant="tertiary"
                                    data-cy="cancel-add-token-button"
                                />
                            </UiStack>
                        ) : (
                            <UiStack className={classes.actionsContainer}>
                                <ApButton
                                    label={translate({ id: 'CLIENT_CANCEL' })}
                                    onClick={returnToPATPage}
                                    variant="tertiary"
                                    data-cy="cancel-add-token-button"
                                />
                                <ApButton
                                    label={translate({ id: 'CLIENT_CREATE' })}
                                    variant="primary"
                                    loading={loading}
                                    onClick={handleSubmit(onSubmit)}
                                    data-cy="token-add-submit-button"
                                    disabled={!tokenNameValue || !expirationValue || !userScopesValue?.length || expirationValue > lifetimeMax}
                                />
                            </UiStack>
                        )
                    }
                >
                    {isViewMode ? (
                        <UiText
                            variant={FontVariantToken.fontSizeH4Bold}
                            className={classes.descriptionText}>
                            {translate({ id: 'CLIENT_VIEW_PERSONAL_ACCESS_TOKEN' })}
                        </UiText>
                    ) : (
                        <UiText
                            variant={FontVariantToken.fontSizeH4Bold}>
                            {translate({ id: 'CLIENT_CREATE_PERSONAL_ACCESS_TOKEN' })}
                        </UiText>
                    )}

                    {!isViewMode && (
                        <UiText
                            className={classes.descriptionText}
                            variant={FontVariantToken.fontSizeM}>
                            {translate({ id: 'CLIENT_CREATE_PERSONAL_ACCESS_TOKEN_DESCRIPTION' })}
                        </UiText>
                    )}

                    {isViewMode && isAdminContext && tokenData?.user && (
                        <UiStack className={classes.ownerFieldContainer}>
                            <ApTextField
                                className={classes.ownerField}
                                label={translate({ id: 'CLIENT_OWNER' })}
                                value={getDisplayName(tokenData.user)}
                                readOnly
                                data-cy="token-owner-field"
                            />
                            <div className={classes.formField} />
                        </UiStack>
                    )}

                    <UiStack className={classes.formFieldsContainer}>
                        <ApTextField
                            className={classes.formField}
                            label={translate({ id: 'CLIENT_NAME' })}
                            data-cy="generate-name"
                            readOnly={isViewMode}
                            value={tokenNameValue || ''}
                            onValueChanged={(e) => setValue('tokenName', e.target.value ?? '', { shouldDirty: true })}
                        />
                        <ApTextField
                            className={classes.formField}
                            label={translate({ id: 'CLIENT_EXPIRATION_DATE' })}
                            type="number"
                            endAdornment={translate({ id: 'CLIENT_DAYS_LONG' })}
                            error={expirationValue > lifetimeMax}
                            helperText={expirationValue > lifetimeMax ? (
                                translate({ id: 'CLIENT_LIFETIME_MESSAGE' }, { 0: lifetimeMax })
                            ) : (
                                translate({ id: 'CLIENT_EXPIRES_ON' }, { 0: timestampToDate(getDateFromNow(expirationValue)) })
                            )}
                            data-cy="generate-expiration"
                            readOnly={isViewMode}
                            value={expirationValue?.toString() || ''}
                            onValueChanged={(e) => {
                                const value = e.target.value ? parseInt(e.target.value) : 0;
                                setValue('expirationDate', value, { shouldDirty: true });
                            }}
                            min={-10}
                            max={lifetimeMax}
                        />
                    </UiStack>

                    <UiStack className={classes.resourcesHeader}>
                        <div>
                            <UiText
                                variant={FontVariantToken.fontSizeLBold}>
                                {translate({ id: 'CLIENT_RESOURCES_HEADING' })}
                            </UiText>
                            <UiText
                                variant={FontVariantToken.fontSizeM}
                                className={classes.resourcesDescription}>
                                {translate({ id: 'CLIENT_ADD_SCOPES_DESCRIPTION' })}
                            </UiText>
                        </div>
                        {!isViewMode && (
                            <ApButton
                                label={translate({ id: 'CLIENT_EXTERNAL_APP_ADD_SCOPES' })}
                                variant="primary"
                                onClick={onAddScopes}
                                data-cy="add-scopes-button"
                            />
                        )}
                    </UiStack>

                    <ApDataGridWrapper<IGridResourceWithId>
                        data={allGridData}
                        selectable={false}
                        dataCy="scopes-ap-data-grid"
                        unpaginated>

                        <ApDataGridColumn<IGridResourceWithId>
                            property='description'
                            title={translate({ id: 'CLIENT_NAME' })}
                            sortable={false}
                            width={10}
                        />

                        <ApDataGridColumn<IGridResourceWithId>
                            property='scopes'
                            title={translate({ id: 'CLIENT_SCOPES' })}
                            sortable={false}
                            width={25}
                            render={(row) => (
                                <UiStack
                                    direction="row"
                                    align="center"
                                    className={classes.scopeCell}>
                                    <UiText className={classes.scopeText}>
                                        {row.userScopes.join(', ')}
                                    </UiText>
                                    {row.userScopes.length > 0 && (
                                        <UiCopyButton
                                            textToCopy={row.userScopes.join(' ')}
                                            data-cy="copy-scopes-button"
                                        />
                                    )}
                                    {!isViewMode && (
                                        <Tooltip title={translate({ id: 'CLIENT_DELETE' })}>
                                            <IconButton
                                                onClick={() => onDeleteScope(row.name)}
                                                data-cy="delete-scopes-button"
                                                size="small"
                                                aria-label={translate({ id: 'CLIENT_DELETE' })}
                                            >
                                                <DeleteForeverOutlinedIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </UiStack>
                            )}
                        />

                        <ApDataGridFooter
                            pageSizes={[ 5, 10, 25, 50 ]}
                        />
                    </ApDataGridWrapper>
                </UiForm>
            </FormProvider>
        </UiPageContainer>
    );
};

const CreatePersonalAccessTokenPageComponent: React.FC = () => {
    const location = useLocation();
    const tokenData = location.state?.tokenData;
    const isDuplicateMode = location.state?.context === 'duplicate';
    const {
        data: externalResources, isLoading,
    } = useSWR<IResource[], Error>(externalApiResourceUrl, getExternalApiResources);

    const duplicateSelectedScopes = useMemo(() => {
        if (!tokenData?.scopes || !isDuplicateMode) {
            return [];
        }

        return mapTokenScopesToResources(tokenData.scopes, externalResources);
    }, [ externalResources, tokenData?.scopes, isDuplicateMode ]);

    const initialSelectedScopes = isDuplicateMode ? duplicateSelectedScopes : [];

    if (isDuplicateMode && isLoading) {
        return (
            <UiStack
                justify="center"
                align="center"
                style={{ flexGrow: 1 }}
            >
                <ApProgressSpinner />
            </UiStack>
        );
    }

    return (
        <>
            <UiDataContextProvider<IPATDataContext> initialState={{ selectedScopes: initialSelectedScopes }}>
                <CreatePersonalAccessTokenForm />
                <UiSuspensefulOutlet />
            </UiDataContextProvider>
        </>
    );
};

export default CreatePersonalAccessTokenPageComponent;
